import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import {
  Table,
  Button,
  message,
  Modal,
  Card,
  Space,
  Form,
  Select,
  Tag,
  Switch,
  Input,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import usePermissions from "../../../hooks/usePermissions";
import PermissionGuard from "../../../shared/ui/PermissionGuard";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import "../../../shared/styles/ListPages.css";

const GetUsers = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get("/users", {
        params: { include_archived: includeArchived ? 1 : 0 },
      });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch {
      message.error(t("usersLoadError"));
    } finally {
      setLoading(false);
    }
  }, [includeArchived, t]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await apiClient.get("/roles");
      setRoles(res.data.roles || []);
    } catch {
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  const roleOptions = useMemo(() => {
    if (roles.length > 0) {
      return roles.map((r) => ({ value: r.name, label: r.display_name }));
    }
    return [
      { value: "admin", label: "Admin" },
      { value: "user", label: "User" },
    ];
  }, [roles]);

  const filteredUsers = useMemo(
    () =>
      filterBySearch(users, searchText, (user) => [
        user.id,
        user.name,
        user.email,
        user.role,
      ]),
    [users, searchText]
  );

  const openCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: "user" });
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      role: user.role,
      password: "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        email: values.email,
        role: values.role,
      };
      if (values.password) payload.password = values.password;

      setSaving(true);
      if (editingUser) {
        await apiClient.put(`/users/${editingUser.id}`, payload);
        message.success(t("userUpdated"));
      } else {
        if (!payload.password) {
          message.error(t("userPasswordRequired"));
          return;
        }
        await apiClient.post("/users", payload);
        message.success(t("userCreated"));
      }

      setModalOpen(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(t("userSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await apiClient.delete(`/users/${archiveTarget.id}`);
      message.success(t("userArchived"));
      setArchiveTarget(null);
      fetchUsers();
    } catch {
      message.error(t("userArchiveError"));
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    try {
      await apiClient.put(`/users/${restoreTarget.id}/restore`);
      message.success(t("userRestored"));
      setRestoreTarget(null);
      fetchUsers();
    } catch {
      message.error(t("userRestoreError"));
    }
  };

  const getRoleDisplay = (roleName) => {
    const roleObj = roles.find((r) => r.name === roleName);
    return roleObj?.display_name || roleName;
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: <b>{t("name")}</b>,
      dataIndex: "name",
      key: "name",
    },
    {
      title: <b>{t("email")}</b>,
      dataIndex: "email",
      key: "email",
    },
    {
      title: <b>{t("role")}</b>,
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color={role === "admin" ? "blue" : "default"}>
          {getRoleDisplay(role)}
        </Tag>
      ),
    },
    {
      title: <b>{t("status")}</b>,
      key: "status",
      render: (_, record) =>
        record.deleted_at ? (
          <Tag color="red">{t("archived")}</Tag>
        ) : (
          <Tag color="green">{t("active")}</Tag>
        ),
    },
    {
      title: <b>{t("actions")}</b>,
      key: "actions",
      render: (_, record) => (
        <Space>
          {hasPermission("users.edit") && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
              disabled={Boolean(record.deleted_at)}
            />
          )}
          {record.deleted_at
            ? hasPermission("users.edit") && (
                <Button
                  type="default"
                  icon={<UndoOutlined />}
                  onClick={() => setRestoreTarget(record)}
                />
              )
            : hasPermission("users.delete") && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setArchiveTarget(record)}
                />
              )}
        </Space>
      ),
    },
  ];

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: "600", fontSize: "1.125rem" }}>
            {t("users")}
          </span>
        }
        variant="borderless"
      >
        <Space
          wrap
          style={{
            width: "100%",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <ListSearch
            value={searchText}
            onChange={setSearchText}
            style={{ width: "100%", maxWidth: 420 }}
            width="100%"
          />
          <Space>
            <span>{t("showArchived")}</span>
            <Switch checked={includeArchived} onChange={setIncludeArchived} />
            <PermissionGuard permission="users.add" hide>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={openCreateModal}
              >
                {t("addUser")}
              </Button>
            </PermissionGuard>
          </Space>
        </Space>

        <Table
          loading={loading}
          dataSource={filteredUsers.map((item) => ({ ...item, key: item.id }))}
          columns={columns}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 900 }}
        />
      </Card>

      {/* Create/Edit User Modal */}
      <Modal
        title={editingUser ? t("editUser") : t("addUser")}
        open={modalOpen}
        onOk={handleSave}
        okText={editingUser ? t("edit") : t("addUser")}
        confirmLoading={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditingUser(null);
          form.resetFields();
        }}
        cancelText={t("cancel")}
        centered
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t("name")}
            rules={[{ required: true, message: t("enterName") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[
              { required: true, message: t("enterEmail") },
              { type: "email", message: t("invalidEmail") },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label={t("role")}
            rules={[{ required: true, message: t("selectRole") }]}
          >
            <Select options={roleOptions} />
          </Form.Item>
          <Form.Item
            name="password"
            label={t("password")}
            rules={
              editingUser
                ? [{ min: 6, message: t("passwordMin") }]
                : [
                    { required: true, message: t("userPasswordRequired") },
                    { min: 6, message: t("passwordMin") },
                  ]
            }
          >
            <Input.Password
              placeholder={editingUser ? t("leaveBlankKeepPassword") : ""}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Archive Confirmation */}
      <Modal
        title={t("confirmArchive")}
        open={Boolean(archiveTarget)}
        onOk={handleArchive}
        onCancel={() => setArchiveTarget(null)}
        okText={t("archive")}
        okButtonProps={{ danger: true }}
        cancelText={t("cancel")}
        centered
      >
        <p>{t("archiveUserConfirm")}</p>
      </Modal>

      {/* Restore Confirmation */}
      <Modal
        title={t("confirmRestore")}
        open={Boolean(restoreTarget)}
        onOk={handleRestore}
        onCancel={() => setRestoreTarget(null)}
        okText={t("restore")}
        cancelText={t("cancel")}
        centered
      >
        <p>{t("restoreUserConfirm")}</p>
      </Modal>
    </div>
  );
};

export default GetUsers;
